import React, { useState, useEffect } from 'react';
import {
  ClientAuthService,
  type ClientPortalAccess,
  type ClientPortalPermissions
} from '../../lib/services/clientAuthService';
import { supabase } from '../../lib/supabase';
import { getSectionText } from '../../lib/utils/task';
import { MinimalLoader } from '../ui/MinimalLoader';
import {
  Shield,
  Plus,
  Settings,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  FolderKanban,
  X,
  AlertCircle,
  Power
} from 'lucide-react';

interface ClientPortalAccessManagerProps {
  clientId: string;
  clientName: string;
  /**
   * Progetto da cui si e' aperta la gestione: viene preselezionato nella creazione di un
   * nuovo accesso, cosi' chi arriva dalla lista o dalla scheda progetto non deve ritrovarlo
   * nell'elenco. Non tocca gli accessi gia' esistenti.
   */
  initialProjectId?: string;
}

/**
 * Sezioni di un progetto, come le vede il cliente nel portale.
 *
 * Non bastano le righe intestazione: i progetti che usano solo le sezioni predefinite
 * (todo, backlog...) non ne hanno nessuna, l'elenco resta vuoto, non si può spuntare niente
 * e il cliente si ritrova il portale senza task. Qui si parte dalle sezioni davvero usate
 * dalle task, e l'intestazione serve solo a dare il nome scelto dall'utente.
 */
async function loadProjectSections(projectId: string): Promise<Array<{ id: string; title: string }>> {
  const { data } = await supabase
    .from('tasks')
    .select('section, title, is_header, order')
    .eq('project_id', projectId)
    .order('order', { ascending: true });

  if (!data) return [];

  const titoli = new Map<string, string>();
  for (const t of data) {
    if (t.is_header && t.section && !titoli.has(t.section)) titoli.set(t.section, t.title);
  }

  const sezioni: Array<{ id: string; title: string }> = [];
  const viste = new Set<string>();
  for (const t of data) {
    const id = t.section;
    if (!id || viste.has(id)) continue;
    viste.add(id);
    sezioni.push({ id, title: titoli.get(id) ?? getSectionText(id) });
  }
  return sezioni;
}

/**
 * Avviso per il progetto senza nessuna sezione spuntata.
 *
 * La RPC `portal_client_project_tasks` e' fail-closed: senza sezioni condivise non restituisce
 * niente (supabase/migrations/20260805_portal_client_time.sql). E' voluto, ma finora non era
 * scritto da nessuna parte e chi creava l'accesso non poteva saperlo.
 * Stile ambra, come gli altri avvisi del CRM. Niente varianti dark: questi modali sono
 * a fondo bianco fisso.
 */
function AvvisoNessunaSezione() {
  return (
    <div className="mt-1 ml-2 rounded-md border border-amber-200 bg-amber-50 p-2">
      <div className="flex gap-2">
        <AlertCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          Nessuna sezione condivisa: il cliente non vedra' alcuna task di questo progetto
        </p>
      </div>
    </div>
  );
}

export function ClientPortalAccessManager({ clientId, clientName, initialProjectId }: ClientPortalAccessManagerProps) {
  const [accesses, setAccesses] = useState<ClientPortalAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<ClientPortalAccess | null>(null);

  const loadAccesses = async () => {
    setLoading(true);
    try {
      const data = await ClientAuthService.getClientAccessList(clientId);
      setAccesses(data);
    } catch (error) {
      console.error('Error loading client accesses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccesses();
  }, [clientId]);

  const handleDelete = async (accessId: string) => {
    if (!confirm('Sei sicuro di voler disabilitare questo accesso? L\'utente non potrà più accedere al portale. Potrai riattivarlo in seguito.')) {
      return;
    }

    try {
      await ClientAuthService.disableClientAccess(accessId);
      await loadAccesses();
    } catch (error) {
      console.error('Error deleting access:', error);
    }
  };

  const handleReactivate = async (accessId: string) => {
    try {
      await ClientAuthService.updateClientAccess(accessId, { is_active: true });
      await loadAccesses();
    } catch (error) {
      console.error('Error reactivating access:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <MinimalLoader size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-600" />
          <h3 className="font-semibold text-gray-900">Accesso Portale Cliente</h3>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Crea accesso
        </button>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
        <p className="text-sm text-emerald-800">
          Da qui puoi creare e gestire l'accesso del cliente al portale. Il cliente potrà visualizzare
          le task dei progetti selezionati, commentare e richiedere nuove attività.
        </p>
      </div>

      {/* Accesses List */}
      {accesses.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <Shield className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 mb-2">Nessun accesso configurato</p>
          <p className="text-sm text-gray-500">
            Crea un accesso per permettere al cliente di visualizzare i suoi progetti
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {accesses.map((access) => (
            <AccessCard
              key={access.id}
              access={access}
              onEdit={() => setShowEditModal(access)}
              onDelete={() => handleDelete(access.id)}
              onReactivate={() => handleReactivate(access.id)}
              onRefresh={loadAccesses}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateAccessModal
          clientId={clientId}
          clientName={clientName}
          initialProjectId={initialProjectId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadAccesses();
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditAccessModal
          access={showEditModal}
          onClose={() => setShowEditModal(null)}
          onUpdated={() => {
            setShowEditModal(null);
            loadAccesses();
          }}
        />
      )}
    </div>
  );
}

// Access Card Component
function AccessCard({
  access,
  onEdit,
  onDelete,
  onReactivate,
  onRefresh
}: {
  access: ClientPortalAccess;
  onEdit: () => void;
  onDelete: () => void;
  onReactivate: () => void;
  onRefresh: () => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedUsername, setCopiedUsername] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState<string | null>(null);

  const portalUrl = `${window.location.origin}/client-portal/login`;

  const copyToClipboard = (text: string, type: 'username' | 'password') => {
    navigator.clipboard.writeText(text);
    if (type === 'username') {
      setCopiedUsername(true);
      setTimeout(() => setCopiedUsername(false), 2000);
    }
  };

  const copyAllCredentials = () => {
    let text = `URL Portale: ${portalUrl}\nUsername: ${access.username}`;
    if (newPassword) {
      text += `\nPassword: ${newPassword}`;
    }
    navigator.clipboard.writeText(text);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleResetPassword = async () => {
    if (!confirm('Sei sicuro di voler resettare la password? L\'utente verrà disconnesso.')) {
      return;
    }

    setResettingPassword(true);
    try {
      const password = generatePassword();
      const success = await ClientAuthService.resetClientPassword(access.id, password);
      if (success) {
        setNewPassword(password);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <div className={`p-4 border rounded-lg ${access.is_active ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{access.username}</span>
            {!access.is_active && (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                Disabilitato
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Ultimo accesso: {access.last_login_at
              ? new Date(access.last_login_at).toLocaleString('it-IT')
              : 'Mai'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Modifica"
          >
            <Settings className="w-4 h-4" />
          </button>
          {access.is_active ? (
            <button
              onClick={onDelete}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Disabilita"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={onReactivate}
              className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
              title="Riattiva accesso"
            >
              <Power className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Projects */}
      <div className="mb-3">
        <p className="text-xs font-medium text-gray-500 mb-1">Progetti accessibili:</p>
        <div className="flex flex-wrap gap-1">
          {access.projects && access.projects.length > 0 ? (
            access.projects.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded"
              >
                <FolderKanban className="w-3 h-3" />
                {p.project?.name || 'Progetto'}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-500">Nessun progetto</span>
          )}
        </div>
      </div>

      {/* Credentials Section */}
      <div className="p-3 bg-gray-50 rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Username:</span>
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono">{access.username}</code>
            <button
              onClick={() => copyToClipboard(access.username, 'username')}
              className="p-1 text-gray-500 hover:text-gray-700"
              title="Copia"
            >
              {copiedUsername ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {newPassword && (
          <div className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded">
            <span className="text-xs text-yellow-800">Nuova password:</span>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono">{newPassword}</code>
              <button
                onClick={() => copyToClipboard(newPassword, 'password')}
                className="p-1 text-yellow-700 hover:text-yellow-900"
                title="Copia"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">URL Portale:</span>
          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
          >
            Apri <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div className="flex gap-2">
          <button
            onClick={copyAllCredentials}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
          >
            {copiedAll ? (
              <>
                <Check className="w-3 h-3 text-green-500" />
                Copiato!
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                Copia credenziali
              </>
            )}
          </button>
          <button
            onClick={handleResetPassword}
            disabled={resettingPassword}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
          >
            {resettingPassword ? (
              <MinimalLoader size="sm" />
            ) : (
              <>
                <RefreshCw className="w-3 h-3" />
                Genera nuova password
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Create Access Modal
function CreateAccessModal({
  clientId,
  clientName,
  initialProjectId,
  onClose,
  onCreated
}: {
  clientId: string;
  clientName: string;
  initialProjectId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [clientProjects, setClientProjects] = useState<Array<{ id: string; name: string; status: string; client_id: string }>>([]);
  const [username, setUsername] = useState(clientName.toLowerCase().replace(/\s+/g, '.'));
  const [password, setPassword] = useState(generatePassword());
  const [selectedProjects, setSelectedProjects] = useState<string[]>(
    initialProjectId ? [initialProjectId] : []
  );
  const [permissions, setPermissions] = useState<ClientPortalPermissions>({
    view_tasks: true,
    request_tasks: true,
    comment_tasks: true,
    view_progress: true,
    view_documents: false,
    view_time: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(true);
  const [created, setCreated] = useState(false);
  const [projectSections, setProjectSections] = useState<Record<string, Array<{ id: string; title: string }>>>({});
  const [selectedSections, setSelectedSections] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const loadClientProjects = async () => {
      const { data } = await supabase
        .from('projects')
        .select('id, name, status, client_id')
        .eq('client_id', clientId)
        .order('name');
      if (data) setClientProjects(data);
    };
    loadClientProjects();
  }, [clientId]);

  // Load sections when a project is selected
  useEffect(() => {
    const loadSections = async () => {
      for (const projectId of selectedProjects) {
        if (!projectSections[projectId]) {
          const sezioni = await loadProjectSections(projectId);
          setProjectSections(prev => ({ ...prev, [projectId]: sezioni }));
        }
      }
    };
    if (selectedProjects.length > 0) loadSections();
  }, [selectedProjects]);

  const handleCreate = async () => {
    if (!username.trim()) {
      setError('Inserisci un username');
      return;
    }
    if (selectedProjects.length === 0) {
      setError('Seleziona almeno un progetto');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const access = await ClientAuthService.createClientAccess({
        client_id: clientId,
        username: username.trim(),
        password,
        project_ids: selectedProjects,
        project_shared_sections: selectedSections,
        permissions
      });

      if (access) {
        setCreated(true);
      } else {
        setError('Errore durante la creazione dell\'accesso');
      }
    } catch (err) {
      setError('Errore durante la creazione dell\'accesso');
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = () => {
    const text = `URL Portale: ${window.location.origin}/client-portal/login\nUsername: ${username}\nPassword: ${password}`;
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {created ? 'Accesso creato!' : 'Crea accesso portale'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {created ? (
          <div className="p-6 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 mb-3">
                L'accesso è stato creato con successo! Condividi queste credenziali con il cliente:
              </p>
              <div className="space-y-2 p-3 bg-white rounded border border-green-200">
                <p className="text-sm"><strong>URL:</strong> {window.location.origin}/client-portal/login</p>
                <p className="text-sm"><strong>Username:</strong> {username}</p>
                <p className="text-sm"><strong>Password:</strong> {password}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={copyCredentials}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Copy className="w-4 h-4" />
                Copia credenziali
              </button>
              <button
                onClick={onCreated}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Chiudi
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="username"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPassword(generatePassword())}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Genera nuova password"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Projects */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Progetti accessibili *
              </label>
              {clientProjects.length === 0 ? (
                <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  Nessun progetto associato a questo cliente
                </p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {clientProjects.map((project) => (
                    <label key={project.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedProjects.includes(project.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProjects([...selectedProjects, project.id]);
                          } else {
                            setSelectedProjects(selectedProjects.filter(id => id !== project.id));
                          }
                        }}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <FolderKanban className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{project.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Sections per project */}
            {selectedProjects.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sezioni visibili al cliente
                </label>
                <div className="space-y-3 p-2 border border-gray-200 rounded-lg">
                  {selectedProjects.map(projectId => {
                    const sections = projectSections[projectId] || [];
                    const scelte = selectedSections[projectId] || [];
                    const project = clientProjects.find(p => p.id === projectId);
                    return (
                      <div key={projectId}>
                        <p className="text-xs font-medium text-gray-500 mb-1">{project?.name}</p>
                        {scelte.length === 0 && <AvvisoNessunaSezione />}
                        <div className="space-y-1 ml-2">
                          {sections.map(section => (
                            <label key={section.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(selectedSections[projectId] || []).includes(section.id)}
                                onChange={(e) => {
                                  const current = selectedSections[projectId] || [];
                                  setSelectedSections({
                                    ...selectedSections,
                                    [projectId]: e.target.checked
                                      ? [...current, section.id]
                                      : current.filter(id => id !== section.id)
                                  });
                                }}
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <Eye className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-sm text-gray-700">{section.title}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-1">Solo le sezioni selezionate saranno visibili nel portale</p>
              </div>
            )}

            {/* Permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permessi
              </label>
              <div className="space-y-2">
                {[
                  { key: 'view_tasks', label: 'Visualizzare task' },
                  { key: 'request_tasks', label: 'Richiedere nuove task' },
                  { key: 'comment_tasks', label: 'Commentare task' },
                  { key: 'view_progress', label: 'Visualizzare progresso' },
                  { key: 'view_documents', label: 'Visualizzare documenti' },
                  { key: 'view_time', label: 'Visualizzare il tempo lavorato' }
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={permissions[key as keyof ClientPortalPermissions]}
                      onChange={(e) => setPermissions({ ...permissions, [key]: e.target.checked })}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleCreate}
                disabled={loading || !username.trim() || selectedProjects.length === 0}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-emerald-400 transition-colors"
              >
                {loading ? 'Creazione...' : 'Crea accesso'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Edit Access Modal
function EditAccessModal({
  access,
  onClose,
  onUpdated
}: {
  access: ClientPortalAccess;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [clientProjects, setClientProjects] = useState<Array<{ id: string; name: string; status: string; client_id: string }>>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>(
    access.projects?.map(p => p.project_id) || []
  );
  const [permissions, setPermissions] = useState<ClientPortalPermissions>(access.permissions);
  const [isActive, setIsActive] = useState(access.is_active);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectSections, setProjectSections] = useState<Record<string, Array<{ id: string; title: string }>>>({});
  const [selectedSections, setSelectedSections] = useState<Record<string, string[]>>(() => {
    const initial: Record<string, string[]> = {};
    for (const p of access.projects || []) {
      if (p.shared_sections && p.shared_sections.length > 0) {
        initial[p.project_id] = [...p.shared_sections];
      }
    }
    return initial;
  });

  useEffect(() => {
    const loadClientProjects = async () => {
      const { data } = await supabase
        .from('projects')
        .select('id, name, status, client_id')
        .eq('client_id', access.client_id)
        .order('name');
      if (data) setClientProjects(data);
    };
    loadClientProjects();
  }, [access.client_id]);

  // Load sections when a project is selected
  useEffect(() => {
    const loadSections = async () => {
      for (const projectId of selectedProjects) {
        if (!projectSections[projectId]) {
          const sezioni = await loadProjectSections(projectId);
          setProjectSections(prev => ({ ...prev, [projectId]: sezioni }));
        }
      }
    };
    if (selectedProjects.length > 0) loadSections();
  }, [selectedProjects]);

  const handleSave = async () => {
    if (selectedProjects.length === 0) {
      setError('Seleziona almeno un progetto');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await ClientAuthService.updateClientAccess(access.id, {
        permissions,
        is_active: isActive,
        project_ids: selectedProjects,
        project_shared_sections: selectedSections
      });

      if (success) {
        onUpdated();
      } else {
        setError('Errore durante l\'aggiornamento');
      }
    } catch (err) {
      setError('Errore durante l\'aggiornamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Modifica accesso
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Username (readonly) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={access.username}
              readOnly
              className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700">Accesso attivo</span>
            </label>
          </div>

          {/* Projects */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Progetti accessibili *
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg">
              {clientProjects.map((project) => (
                <label key={project.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedProjects.includes(project.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProjects([...selectedProjects, project.id]);
                      } else {
                        setSelectedProjects(selectedProjects.filter(id => id !== project.id));
                      }
                    }}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <FolderKanban className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{project.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Sections per project */}
          {selectedProjects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sezioni visibili al cliente
              </label>
              <div className="space-y-3 p-2 border border-gray-200 rounded-lg">
                {selectedProjects.map(projectId => {
                  const sections = projectSections[projectId] || [];
                  const scelte = selectedSections[projectId] || [];
                  const project = clientProjects.find(p => p.id === projectId);
                  return (
                    <div key={projectId}>
                      <p className="text-xs font-medium text-gray-500 mb-1">{project?.name}</p>
                      {scelte.length === 0 && <AvvisoNessunaSezione />}
                      <div className="space-y-1 ml-2">
                        {sections.map(section => (
                          <label key={section.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(selectedSections[projectId] || []).includes(section.id)}
                              onChange={(e) => {
                                const current = selectedSections[projectId] || [];
                                setSelectedSections({
                                  ...selectedSections,
                                  [projectId]: e.target.checked
                                    ? [...current, section.id]
                                    : current.filter(id => id !== section.id)
                                });
                              }}
                              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <Eye className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm text-gray-700">{section.title}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1">Solo le sezioni selezionate saranno visibili nel portale</p>
            </div>
          )}

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permessi
            </label>
            <div className="space-y-2">
              {[
                { key: 'view_tasks', label: 'Visualizzare task' },
                { key: 'request_tasks', label: 'Richiedere nuove task' },
                { key: 'comment_tasks', label: 'Commentare task' },
                { key: 'view_progress', label: 'Visualizzare progresso' },
                { key: 'view_documents', label: 'Visualizzare documenti' },
                { key: 'view_time', label: 'Visualizzare il tempo lavorato' }
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={permissions[key as keyof ClientPortalPermissions]}
                    onChange={(e) => setPermissions({ ...permissions, [key]: e.target.checked })}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={loading || selectedProjects.length === 0}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-emerald-400 transition-colors"
            >
              {loading ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to generate random password
function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < 10; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}
